import { Injectable, inject } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { firstValueFrom } from 'rxjs';
import type { LosKvRecord } from './los-indexed-db.config';

/**
 * Thin key/value adapter over ngx-indexed-db for Opportunity shell persistence
 * and other module-local IndexedDB keys.
 */
@Injectable({ providedIn: 'root' })
export class LosPersistenceKvService {
  private readonly db = inject(NgxIndexedDBService);
  private readonly cache = new Map<string, string>();

  /** Read a value, using an in-memory cache when already loaded in this tab. */
  async get(key: string): Promise<string | null> {
    if (this.cache.has(key)) {
      return this.cache.get(key) ?? null;
    }
    try {
      const record = await firstValueFrom(this.db.getByKey<LosKvRecord>('kv', key));
      const value = record?.value ?? null;
      if (value != null) {
        this.cache.set(key, value);
      }
      return value;
    } catch {
      return null;
    }
  }

  getCached(key: string): string | null {
    return this.cache.get(key) ?? null;
  }

  set(key: string, value: string): void {
    this.cache.set(key, value);
    void this.persist(key, value);
  }

  remove(key: string): void {
    this.cache.delete(key);
    void firstValueFrom(this.db.deleteByKey('kv', key)).catch(() => undefined);
  }

  clearMemoryCache(): void {
    this.cache.clear();
  }

  async removeKeysWithPrefix(prefix: string): Promise<void> {
    await this.removeKeysWithPrefixes([prefix]);
  }

  async removeKeysWithPrefixes(prefixes: string[]): Promise<void> {
    const normalized = prefixes.map((p) => p.trim()).filter(Boolean);
    if (!normalized.length) {
      return;
    }
    try {
      const records = await firstValueFrom(this.db.getAll<LosKvRecord>('kv'));
      const keys = records
        .map((record) => record.key)
        .filter((key) => normalized.some((prefix) => key.startsWith(prefix)));
      for (const key of keys) {
        this.cache.delete(key);
        await firstValueFrom(this.db.deleteByKey('kv', key));
      }
    } catch {
      // ignore
    }
  }

  private async persist(key: string, value: string): Promise<void> {
    try {
      const record: LosKvRecord = { key, value };
      const existing = await firstValueFrom(this.db.getByKey<LosKvRecord>('kv', key)).catch(
        () => null,
      );
      if (existing) {
        await firstValueFrom(this.db.update('kv', record));
      } else {
        await firstValueFrom(this.db.add('kv', record));
      }
    } catch {
      // ignore quota / private mode
    }
  }
}
